import PropTypes from 'prop-types';
import { ConfigProvider } from 'antd';
import { Locale } from '@fixter/i18n';
import { queryCmsContent } from '$server/modules/cms';
import nextFetchRequest from '$api/nextFetchRequest';
import deferHydration from '$util/deferHydration';
import { getServerLogger } from '$util/index';
import MainLayout from '$layout/MainLayout';
import { OurServicesTopSection } from '$components/pages/our-services/OurServicesTopSection';
import { getReassuranceQuery } from '$components/composite/Reassurance/gql';
import { QUERY_GET_FOOTER_CMS_CONTENT } from '$components/layout/Footer/gql/queries/getFooterCmsContent';
import { QUERY_READ_FOOTER_DATA } from '$components/layout/Footer/gql/queries/readFooterData';
import { getSearchBoxQuery } from '$components/composite/SearchSuggestions/gql';
import { getSocialTrustQuery } from '$components/SocialTrustSection/gql';
import { getNavbarQuery } from '$commons/gql';
import { getVrmLookupQuery } from '$quantum/molecules/VrmLookup/gql';
import { getTeamSupportSection } from '$quantum/organisms/TeamSupportSection/gql';
import { getPromotionBannerQuery } from '$quantum/molecules/PromotionBanner/gql';
import { GET_CUSTOMER_SERVICE_CMS_QUERY } from '$cms/gql';
import { GET_HOMEPAGE_OPEN_GRAPH_CONTENT, GET_DOMAIN_VERIFICATION_CONTENT } from '$layout/MainLayout/gql';
import { isPreviewActive } from '$server/modules/cms/util';
import { getLocaleFromHostname } from '$util/domains';
import { WithResponsiveGlobalStyles } from '$util/theme/withResponsive';
import { CACHE_CONTROL_CACHE_ENABLED, CACHE_CONTROL_NO_CACHE } from '../internals/next/config/headers';

const PAGE_NAME = 'our-services';

const OurServicesSection = deferHydration(() => import('$components/OurServicesSection'), {
  hydrationId: '$components/OurServicesSection',
  trigger: 'view',
});

const TeamSupportSection = deferHydration(() => import('$quantum/organisms/TeamSupportSection'), {
  hydrationId: '$components/TeamSupportSection',
  trigger: 'view',
});

const TryFixterSection = deferHydration(() => import('$components/TryFixterSection'), {
  hydrationId: '$components/TryFixterSection',
  trigger: 'view',
});

const TagsSection = deferHydration(() => import('$components/TagsSection'), {
  hydrationId: '$components/TagsSection',
  trigger: 'view',
});

const BreadcrumbsSection = deferHydration(() => import('$components/BreadcrumbsSection'), {
  hydrationId: '$components/BreadcrumbsSection',
  trigger: 'view',
});

const BookingStepsSection = deferHydration(() => import('$components/BookingStepsSection'), {
  hydrationId: '$components/BookingStepsSection',
  trigger: 'view',
});

const ReassuranceBlueIconsTheme = deferHydration(() => import('$components/ReassuranceBlueIconsTheme'), {
  hydrationId: '$components/ReassuranceBlueIconsTheme',
  trigger: 'view',
});

const SocialTrustSection = deferHydration(() => import('$components/SocialTrustSection'), {
  hydrationId: '$components/SocialTrustSection',
  trigger: 'view',
});

function WorkOperationsPage({ workOperationsData, tagsList }) {
  return (
    <ConfigProvider
      theme={{
        cssVar: true,
        hashed: false,
        token: { fontFamily: 'Rubik, sans-serif' },
      }}
    >
      <MainLayout
        className="WorkOperationsPage"
        seo={{
          noindex: false,
          nofollow: false,
          title: 'Car service, MOT and repair the easy way | Fixter',
          metaDescription:
            `Looking for hassle-free MOT, car service, repair or maintenance? ` +
            `Let Fixter take the strain! Our local experts will collect and deliver ` +
            `your car to your door and make sure you get the right job done at a fair price.`,
        }}
      >
        <WithResponsiveGlobalStyles />
        <OurServicesTopSection />
        <SocialTrustSection />
        <ReassuranceBlueIconsTheme />
        <OurServicesSection workOperations={workOperationsData} />
        <BookingStepsSection />
        <TryFixterSection />
        <TagsSection tagsList={tagsList} />
        <TeamSupportSection source={PAGE_NAME} />
        <BreadcrumbsSection />
      </MainLayout>
    </ConfigProvider>
  );
}

export const getServerSideProps = async function ({ req, res, query: urlQuery, preview: isPreviewMode }) {
  const log = getServerLogger(PAGE_NAME);
  const locale = getLocaleFromHostname(req.headers.host || '');
  if (locale !== Locale.EN_GB) {
    return {
      notFound: true,
    };
  }

  const serverParams = {
    pathname: 'our-services',
    pageType: [],
    pageTypeFromPath: 'workOperations',
  };
  const { pageType, pageTypeFromPath } = serverParams;
  log.debug({ pageType, pageTypeFromPath }, 'getServerSideProps for page %s', PAGE_NAME);

  // eslint-disable-next-line no-template-curly-in-string
  const bindings = eval('require(`${SERVER_PATH}/bindings`)();');
  const { getWorkOperationsData } = bindings.newObject('store.pageContent');

  const preview = isPreviewActive(urlQuery, isPreviewMode);
  const cmsContent = await queryCmsContent(PAGE_NAME, {
    cmsQuery: [
      GET_HOMEPAGE_OPEN_GRAPH_CONTENT,
      GET_DOMAIN_VERIFICATION_CONTENT,
      getNavbarQuery,
      getVrmLookupQuery,
      QUERY_GET_FOOTER_CMS_CONTENT,
      getReassuranceQuery,
      getSearchBoxQuery,
      getSocialTrustQuery,
      GET_CUSTOMER_SERVICE_CMS_QUERY,
      getTeamSupportSection,
      getPromotionBannerQuery,
    ],
    resQuery: [QUERY_READ_FOOTER_DATA],
    context: {
      variables: { locale },
      preview,
    },
  });

  const nextFetch = nextFetchRequest(req);
  // request data in parallel
  const [workOperationsData = [], tagsList = {}] = await Promise.all([
    getWorkOperationsData(),
    // for SEO purposes tags links are fetched server-side
    nextFetch('/api/tags-list', { pathnameDataList: pageType, pathnameCombination: pageTypeFromPath }).catch(
      (error) => {
        // don't crash page if there's an error
        log.error({ error }, 'Failed to fetch tags-list');
        return {};
      }
    ),
  ]);

  if (preview) res.setHeader('Cache-Control', CACHE_CONTROL_NO_CACHE);
  else res.setHeader('Cache-Control', CACHE_CONTROL_CACHE_ENABLED);

  return { props: { serverParams, workOperationsData, tagsList, cmsContent } };
};

WorkOperationsPage.propTypes = {
  workOperationsData: PropTypes.array.isRequired,
  tagsList: PropTypes.object.isRequired,
};

export default WorkOperationsPage;
